/**
 * Google Map picker: click on map to set station location.
 * On click: reverse geocode → address + lat/lng passed to onSelect (same shape as DaData).
 * Uses shared GoogleMapsProvider (single script load) to avoid white screen when opening dialog.
 * Geocoding language: follows app locale (e.g. ru for Russia/Belarus).
 */
import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { useGoogleMaps } from '../GoogleMapsProvider';

const DEFAULT_CENTER = { lat: 53.9, lng: 27.56 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '320px', borderRadius: 8 };

/**
 * @param {number} lat
 * @param {number} lng
 * @param {string} [language] - IETF language tag (e.g. 'ru', 'en'). Affects address language for reverse geocode.
 */
function reverseGeocode(lat, lng, language) {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
      reject(new Error('Google Maps not loaded'));
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    const request = {
      location: { lat, lng },
      ...(language && { language }),
    };
    geocoder.geocode(request, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        reject(new Error(status || 'No results'));
        return;
      }
      resolve(results[0].formatted_address || '');
    });
  });
}

export default function GoogleMapPicker({ center, onSelect, disabled }) {
  const { t, i18n } = useTranslation();
  const { isLoaded, loadError, apiKey } = useGoogleMaps();

  const [markerPosition, setMarkerPosition] = React.useState(null);
  const [selecting, setSelecting] = React.useState(false);

  /** Язык адреса при reverse geocode: по текущей локали (ru → русский для РФ/Беларуси) */
  const geocodeLanguage = React.useMemo(() => {
    const lang = (i18n.language || '').toLowerCase().split(/[-_]/)[0];
    return lang || 'en';
  }, [i18n.language]);

  const currentCenter = React.useMemo(() => {
    if (center?.lat != null && center?.lng != null) {
      const lat = Number(center.lat);
      const lng = Number(center.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
    return DEFAULT_CENTER;
  }, [center?.lat, center?.lng]);

  React.useEffect(() => {
    if (center?.lat != null && center?.lng != null) {
      const lat = Number(center.lat);
      const lng = Number(center.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        setMarkerPosition({ lat, lng });
      } else {
        setMarkerPosition(null);
      }
    } else {
      setMarkerPosition(null);
    }
  }, [center?.lat, center?.lng]);

  const handleMapClick = React.useCallback(
    (e) => {
      if (disabled || !onSelect) return;
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;
      setMarkerPosition({ lat, lng });
      setSelecting(true);
      reverseGeocode(lat, lng, geocodeLanguage)
        .then((address) => {
          onSelect({
            address: address || '',
            latitude: String(lat),
            longitude: String(lng),
          });
        })
        .catch(() => {
          onSelect({
            address: '',
            latitude: String(lat),
            longitude: String(lng),
          });
        })
        .finally(() => setSelecting(false));
    },
    [disabled, onSelect, geocodeLanguage],
  );

  if (!apiKey) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('stationPage.mapSetLocationTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('stationPage.mapApiKeyMissing')}
        </Typography>
      </Paper>
    );
  }

  if (loadError) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('stationPage.mapSetLocationTitle')}
        </Typography>
        <Typography variant="body2" color="error">
          {t('stationPage.mapLoadError')}
        </Typography>
      </Paper>
    );
  }

  if (!isLoaded) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('stationPage.mapSetLocationTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('stationPage.mapLoading')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('stationPage.mapSetLocationTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('stationPage.mapSetLocationHint')}
      </Typography>
      <Box sx={{ overflow: 'hidden', borderRadius: 1, opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={currentCenter}
          zoom={markerPosition ? 17 : 12}
          onClick={handleMapClick}
          options={{ disableDefaultUI: false, zoomControl: true, scrollwheel: true }}
        >
          {markerPosition && <Marker position={markerPosition} />}
        </GoogleMap>
      </Box>
      {selecting && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t('stationPage.mapGettingAddress')}
        </Typography>
      )}
    </Paper>
  );
}

GoogleMapPicker.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
